<template>
  <div class="relative-position" :class="auth.iframeSrc ? 'map-iframe' : ''">
    <div
      class="d-flex justify-content-center mb-4 mt-3 iframe-control"
      v-if="auth.iframeSrc"
    >
      <button @click="goToReferrals()" class="btn btn-outline-primary mr-3">
        <i class="fas fa-envelope-open-text"></i> Referrals
      </button>
      <button @click="doLogout()" class="btn btn-outline-danger">
        <i class="fas fa-sign-out-alt"></i> Logout
      </button>
    </div>
    <div id="map" class="map" ref="map"></div>
    <div class="text-center detect-location-container">
      <div
        class="py-2 px-4 rounded alert alert-warning font-weight-bold blink-opacity"
        v-if="locationWarningMessage"
      >
        {{ locationWarningMessage }}
      </div>
    </div>
    <div
      class="search-box mt-3 mb-4 ml-auto mr-auto col-10 col-sm-10 col-md-10 col-xl-8 p-0 d-flex flex-column justify-content-center"
      :class="isSearchFocused ? 'focused' : ''"
      v-click-outside="searchBlured"
    >
      <div class="input-group align-items-center">
        <input
          type="text"
          class="form-control search-input rounded-left"
          v-model="searchText"
          data-v-step="vendorMapSearch"
          :disabled="speaking || loading"
          @focus="(isSearchFocused = true), closeInfoPopup()"
          :placeholder="
            searchByCity
              ? 'Search by country, city, zip or address.'
              : 'What kind of health care services are you looking for? (Search by service, product, vendor name or key words).'
          "
          :aria-label="
            searchByCity
              ? 'Search by country, city, zip or address.'
              : 'What kind of health care services are you looking for? (Search by service, product, vendor name or key words).'
          "
          @input="removeError"
          @keyup.enter="searchVendors(true)"
        />
        <i
          class="fas fa-times cancel-icon"
          v-if="searchText.trim().length && !speaking"
          @click="clearSearch"
        ></i>
        <google-speech-to-text
          class="speech-btn"
          @speechend="speechEnd"
          @speechupdate="speechUpdate"
          @speechruning="(val) => ((speaking = val), closeInfoPopup())"
          :disabled="loading"
        />
        <button
          class="btn search-button border-0 rounded-circle"
          type="button"
          :disabled="loading"
          @click="!loading && searchVendors(true)"
        >
          <i class="fas fa-search search-icon" v-if="!loading"></i>
          <i
            class="fas fa-lg fa-spin fa-circle-notch text-primary"
            v-if="loading"
          ></i>
        </button>
        <button
          type="button"
          data-v-step="searchCity"
          class="btn ml-2 search-settings-btn"
          data-toggle="dropdown"
          aria-haspopup="true"
          aria-expanded="false"
        >
          <i class="fas fa-cog"></i>
        </button>
        <div
          class="dropdown-menu search-setting-menu"
          @click="$event.stopPropagation()"
        >
          <h3 class="mx-3 my-2">
            <i class="fas fa-cog mr-2"></i>Advanced Settings
          </h3>
          <div class="dropdown-divider"></div>
          <div class="dropdown-item">
            <div class="custom-control custom-switch cursor-pointer">
              <input
                type="checkbox"
                class="custom-control-input cursor-pointer"
                id="city-search"
                v-model="searchByCity"
                @change="
                  (errorText = ''), (hasError = false), (followResults = false)
                "
              />
              <label
                class="custom-control-label cursor-pointer"
                for="city-search"
                >Search By City</label
              >
            </div>
          </div>
          <div class="dropdown-item">
            <div class="custom-control custom-switch cursor-pointer">
              <input
                type="checkbox"
                class="custom-control-input cursor-pointer"
                id="follow-results"
                :disabled="searchByCity"
                v-model="followResults"
              />
              <label
                class="custom-control-label cursor-pointer"
                for="follow-results"
                >Auto Zoom Detect</label
              >
            </div>
          </div>
          <div class="dropdown-item">
            <div class="custom-control custom-switch cursor-pointer">
              <input
                type="checkbox"
                class="custom-control-input cursor-pointer"
                id="marker-click-zoom"
                v-model="markerClickZoom"
              />
              <label
                class="custom-control-label cursor-pointer"
                for="marker-click-zoom"
                >Zoom on Marker Click</label
              >
            </div>
          </div>
          <div class="dropdown-item">
            <div class="custom-control custom-switch cursor-pointer">
              <input
                type="checkbox"
                class="custom-control-input cursor-pointer"
                id="clickable-icons-control"
                v-model="allowKnownClick"
                @change="updateClickableIcons"
              />
              <label
                class="custom-control-label cursor-pointer"
                for="clickable-icons-control"
                >Display Known Places Info</label
              >
            </div>
          </div>
        </div>
        <i
          class="fa fa-info-circle mobile-only ml-2 tooltip-info cursor-pointer"
          v-tooltip="
            searchByCity
              ? 'Search by country, city, zip or address'
              : 'What kind of health care services are you looking for? (Search by service, product, vendor name or key words).'
          "
        ></i>
      </div>
      <div class="text-center">
        <div
          class="invalid-feedback alert py-1 alert-danger mx-0 my-1"
          v-if="hasError"
        >
          {{ errorText }}
        </div>
      </div>
    </div>
    <button
      @click="goToCurrentLocation"
      class="btn current-location-btn p-2"
      v-tooltip="'Return to my location'"
    >
      <img class="current-location-img" src="/images/location.png" />
    </button>
    <div class="my-4 vendor-cards" ref="vendorsCards">
      <div
        class="row p-0 m-0 ml-md-4 justify-content-between align-items-center overflow-visible"
      >
        <h1 class="pt-3 m-0 col-12 col-md-6 col-lg-4 mb-5">
          C3 Healthcare Vendors
        </h1>
        <div
          class="dropdown col-12 col-md-6 col-lg-8 text-right mb-5"
          v-if="user.isAdmin || user.isVendor"
        >
          <button
            class="btn btn-secondary dropdown-toggle col-12 col-md-auto"
            type="button"
            id="dropdownSelectVendorMenu"
            data-toggle="dropdown"
            aria-haspopup="true"
            aria-expanded="false"
          >
            {{
              selectedUserVendor
                ? selectedUserVendor.name
                : "Select a vendor for Networking"
            }}
          </button>
          <div
            class="dropdown-menu select-vendor-menu p-0"
            aria-labelledby="dropdownSelectVendorMenu"
          >
            <alert v-if="loading" type="loader" class="mb-0" />
            <alert
              v-if="!loading && !myVendors.length"
              type="loader"
              class="mb-0"
              >You are not a primary contact for any vendor</alert
            >
            <a
              v-for="(vendor, index) in myVendors"
              :class="`dropdown-item py-3 ${index ? 'border-top' : ''}`"
              :key="vendor.id"
              href="#"
              @click="setSelectedUserVendor($event, vendor)"
              >{{ vendor.name }}</a
            >
          </div>
        </div>
      </div>
      <alert v-if="loading" type="loader" color="secondary" />
      <div class="card-column" ref="mainVendorCard">
        <vendor-card
          :contentClass="movingToCard ? 'blink-shadow' : ''"
          :vendor="selectedVendor"
          :selectedUserVendor="selectedUserVendor"
          :showInfo="true"
          :center="latLng"
          v-if="selectedVendor && !ignoreSelectedVendor"
          :searchText="
            resultVendors &&
            activeVendors.length !== resultVendors.length &&
            submittedSearchtext
          "
          @show-vendor="showVendor(selectedVendor)"
          @view-vendor-profile="viewVendorProfile(selectedVendor)"
        />
        <vendor-card
          v-for="v in resultVendors"
          :key="`${v.id}_${v.order}`"
          :vendor="v"
          :center="latLng"
          :searchText="
            resultVendors &&
            activeVendors.length !== resultVendors.length &&
            submittedSearchtext
          "
          :selectedUserVendor="selectedUserVendor"
          @show-vendor="showVendor(v)"
          @view-vendor-profile="viewVendorProfile(v)"
        />
      </div>
      <div
        class="card p-4"
        v-if="
          resultVendors &&
          resultVendors.length === 0 &&
          activeVendors.length !== resultVendors.length &&
          !(selectedVendor && !ignoreSelectedVendor)
        "
      >
        No search results found.
      </div>
    </div>
    <modal id="vendor-profile-modal">
      <div class="row vendor-info-row" v-if="selectedVendorToShow">
        <div class="col vendor-logo-col">
          <img :src="vendorProfileLogo" class="rounded-circle w-100" />
        </div>
        <div class="col-12 col-md-10">
          <h1 class="header-title bold text-capitalize">
            {{ selectedVendorToShow.name }}
          </h1>
          <p class="text-dark mt-2 bio-text">
            <icon class="mr-3" type="align-left" />
            {{ selectedVendorToShow.bio }}
          </p>
          <div class="vendor-links-container">
            <a
              :href="
                selectedVendorToShow.site.indexOf('http') > -1
                  ? selectedVendorToShow.site
                  : `https://${selectedVendorToShow.site}`
              "
              target="_blank"
              v-if="selectedVendorToShow.site"
            >
              <icon class="mr-1" type="globe" />
              {{ selectedVendorToShow.site }}
            </a>
            <br v-if="selectedVendorToShow.site" />
            <a
              :href="`mailto:${selectedVendorToShow.email}`"
              v-if="selectedVendorToShow.email"
            >
              <icon class="mr-1" type="envelope" />
              {{ selectedVendorToShow.email }}
            </a>
            <br v-if="selectedVendorToShow.email" />
            <a
              :href="selectedVendorToShow.intakeFormInfo"
              v-if="selectedVendorToShow.intakeFormInfo"
            >
              <icon class="mr-1" type="file-download" />
              Download Intake Form
            </a>
          </div>
          <div class="relative-position">
            <button
              class="btn btn-secondary mt-2"
              @click="getDirections"
              v-click-outside="hideDropdownList"
            >
              Get Directions
            </button>
            <button
              class="btn mt-2 ml-2 btn-primary"
              v-if="selectedVendorToShow.booking_link"
              @click="openBooking(selectedVendorToShow.booking_link)"
            >
              Book an Appointment
            </button>
            <ul
              v-if="getDirectionsClicked"
              class="list-group directions-list card shadow-card"
            >
              <li
                :key="index"
                v-for="(item, index) in selectedVendorToShow.locations"
                class="list-group-item bg-white rounded-0 cursor-pointer list_item"
                @click="getDirectionsFor(item)"
              >
                {{ getAddress(item) }}
              </li>
            </ul>
          </div>
        </div>
        <div
          class="border-top w-100 mt-4 pt-4"
          v-if="
            selectedVendorToShow.services &&
            selectedVendorToShow.services.length
          "
        >
          <h3 class="mb-0 ml-2">Services/Specialties</h3>
          <div class="card mt-3">
            <ul class="service-items d-flex flex-wrap p-3">
              <li
                class="service-item col-12 col-sm-6 col-md-4 d-flex align-items-center justify-content-center"
                v-for="service in selectedVendorToShow.services"
                :key="service.id"
              >
                <div
                  class="service-item-container text-center d-flex w-100 align-items-center justify-content-center"
                >
                  {{ service.name }}
                </div>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </modal>

    <button
      class="back-to-top-btn btn btn-secondary rounded-pill"
      @click="scrollToElement()"
    >
      Show {{ areaShown == "vendors" ? "Map" : "Vendor Cards" }}
    </button>
    <loader v-if="secretRunning"> </loader>
  </div>
</template>
<script>
import Vue from "vue";
import gmapsInit from "@/utils/gmaps";
import { mapState, mapActions } from "vuex";
import { default as VendorCard } from "../map/partials/VendorCard";
import MapMarkerInfo from "./partials/MapMarkerInfo.vue";
import GoogleSpeechToText from "../../components/GoogleSpeechToText.vue";
import constants from "./constants";
import axios from "axios";
import Auth from "../../auth";
import Loader from "../../components/Loader.vue";
import { MarkerClusterer } from "@googlemaps/markerclusterer";

let google = null;
let coder = null;
let map = null;
let markerCluster = null;
let infowindow = null;

export default {
  name: "Map",
  components: {
    VendorCard,
    GoogleSpeechToText,
    Loader,
  },
  data() {
    return {
      currentLocation: null,
      movingToCard: false,
      speaking: false,
      searchByCity: false,
      markerClickZoom: false,
      isSearchFocused: false,
      selectedVendor: false,
      selectedVendorToShow: false,
      secretRunning: false,
      allowKnownClick: false,
      errorText: "Please enter at least 3 characters.",
      locationIndex: 0,
      resultVendors: null,
      ignoreSelectedVendor: false,
      searchText: "",
      submittedSearchtext: "",
      hasError: false,
      closeZoom: 14,
      defaultZoom: 12,
      mediumZoom: 9,
      coder: null,
      mapSm: false,
      latLng: false,
      vendorAvatars: constants.AVATARS,
      areaShown: "",
      getDirectionsClicked: false,
      followResults: false,
      mapMarkers: [],
      selectedUserVendor: null,
      locationWarningMessage: null,
      firstMapTypeChange: true,
      drawingManager: null,
      drawnShapes: [],
    };
  },
  computed: {
    ...mapState(["vendors", "loading", "auth"]),
    user: function () {
      return Auth.user.get();
    },
    storedSelectedVendor: (state) => state.vendors.selectedVendor,
    activeVendors: function () {
      return this.vendors.data.filter((v) => {
        if (v.active) {
          return v;
        }
      });
    },
    myVendors: function () {
      return this.vendors.data.filter((v) => {
        if (
          v.active &&
          (this.user.isAdmin || (v.admins || []).includes(this.user.id))
        ) {
          return v;
        }
      });
    },
    vendorProfileLogo: function () {
      if (this.selectedVendorToShow && this.selectedVendorToShow.logo) {
        return this.selectedVendorToShow.logoUrl;
      }
      return `/images/logo-gfx.png`;
    },
  },
  created() {
    this.handleDebouncedScroll = _.debounce(this.handleScroll, 100);
    window.addEventListener("scroll", this.handleDebouncedScroll);
  },
  beforeDestroy() {
    window.removeEventListener("scroll", this.handleDebouncedScroll);
  },
  async mounted() {
    this.handleScroll();
    this.vendors.data = [];
    try {
      google = await gmapsInit();
      coder = new google.maps.Geocoder();
      map = new google.maps.Map(document.getElementById("map"), {
        ...this.getMapOptions(),
        zoom: this.defaultZoom,
      });

      this.handleMapViewChange();
      infowindow = new google.maps.InfoWindow({
        maxWidth: 500,
        pixelOffset: new google.maps.Size(252, 80),
      });
      this.plotMarkers();
      this.geocode();
      this.$store.dispatch("vendors/all").then((vendors) => {
        if (vendors) {
          this.resultVendors = vendors;
        }
        this.plotMarkers();
      });
      this.initDrawing();
    } catch (error) {
      //eslint-disable-next-line
      console.log(error);
    }
  },
  watch: {
    "vendors.data": function () {
      if (!this.selectedUserVendor && this.storedSelectedVendor) {
        this.selectedUserVendor = this.storedSelectedVendor;
      }
    },
  },
  methods: {
    initDrawing: function () {
      this.drawingManager = new google.maps.drawing.DrawingManager({
        drawingControl: false,
      });
    },
    ...mapActions({
      doLogout: "auth/logout",
      setSelectedVendor: "vendors/setSelectedVendor",
    }),
    getMapOptions: function () {
      return {
        zoomControl: true,
        mapTypeControl: true,
        minZoom: 0,
        maxZoom: 22,
        heading: 360,
        tilt: 47.5,
        mapId: "c71a525017d6be1f",
        scaleControl: true,
        streetViewControl: true,
        rotateControl: true,
        keyboardShortcuts: false,
        disableDefaultUI: true,
        mapTypeControlOptions: {
          style: google.maps.MapTypeControlStyle.DROPDOWN_MENU,
          mapTypeIds: [
            google.maps.MapTypeId.ROADMAP,
            google.maps.MapTypeId.SATELLITE,
            google.maps.MapTypeId.HYBRID,
            google.maps.MapTypeId.TERRAIN,
          ],
        },
        isFractionalZoomEnabled: true,
        clickableIcons: this.allowKnownClick, // allow clicking on known places
      };
    },
    updateClickableIcons: function () {
      map.setClickableIcons(this.allowKnownClick);
    },
    setSelectedUserVendor: function (event, vendor) {
      event.preventDefault();
      this.selectedUserVendor = vendor;
      this.setSelectedVendor(vendor);
    },
    closeInfoPopup: function () {
      if (infowindow && map) {
        infowindow.close(map);
      }
    },
    searchBlured: function () {
      this.isSearchFocused = false;
      this.hasError = false;
    },
    refreshMap: function (keepCurrentLocation = false) {
      const currentLocation = map.center;
      const currentZoom = map.zoom;
      coder = new google.maps.Geocoder();
      if (keepCurrentLocation && currentLocation) {
        map.setZoom(currentZoom);
        map.setCenter({
          lat: currentLocation.lat(),
          lng: currentLocation.lng(),
        });
      } else {
        map.setZoom(this.defaultZoom);
      }
      this.plotMarkers();
    },
    goToCurrentLocation: function () {
      this.geocode(true);
    },
    geocode: function (withZoom = false) {
      if (navigator.geolocation) {
        if (this.locationWarningMessage) {
          return;
        }
        this.locationWarningMessage =
          "Please wait while C3 identifies your exact location...";
        if (!this.currentLocation) {
          navigator.geolocation.getCurrentPosition(
            (position) => {
              this.currentLocation = {
                lat: position.coords.latitude,
                lng: position.coords.longitude,
              };
              this.locationWarningMessage = null;
              map.setCenter(this.currentLocation);
              if (withZoom) {
                map.setZoom(this.defaultZoom);
              }
              this.selectedVendor = false;
              this.plotMarkers();
            },
            (error) => {
              this.locationWarningMessage = error.message;
              coder.geocode(
                { address: "Toronto, Canada" },
                (results, status) => {
                  if (status !== "OK" || !results[0]) {
                    throw new Error(status);
                  }
                  map.setCenter(results[0].geometry.location);
                  this.selectedVendor = false;
                  this.plotMarkers();
                  setTimeout(() => {
                    this.locationWarningMessage = null;
                  }, 5000);
                }
              );
            },
            {
              enableHighAccuracy: false,
              timeout: 10000,
              maximumAge: Infinity,
            }
          );
        } else {
          this.locationWarningMessage = null;
          map.setCenter(this.currentLocation);
          if (withZoom) {
            map.setZoom(this.defaultZoom);
          }
          this.selectedVendor = false;
        }
      } else {
        console.log("Browser doesn't support geolocation!");
        this.locationWarningMessage = null;
        coder.geocode({ address: "Toronto, Canada" }, (results, status) => {
          if (status !== "OK" || !results[0]) {
            throw new Error(status);
          }
          map.setCenter(results[0].geometry.location);
          this.selectedVendor = false;
          this.plotMarkers();
        });
      }
    },
    markerClickHandler: function (marker) {
      if (this.markerClickZoom) {
        map.setZoom(this.closeZoom);
      }
      this.showVendor(marker.vendor, true);
      map.panTo(marker.getPosition());
    },
    plotMarkers: function () {
      let root = this;
      root.mapMarkers.forEach((marker) => {
        marker.setMap(null);
      });
      root.mapMarkers = [];
      const hash = {};
      let vm = this;
      if (this.resultVendors) {
        this.resultVendors.filter(
          function (v) {
            if (v.locations.length > 0 && v.active) {
              v.locations.filter(function (l) {
                const key = l.lat + "" + l.lng;
                const offset = hash[key];
                if (offset >= 0) {
                  hash[key] = hash[key] + 0.000008;
                } else {
                  hash[key] = 0;
                }
                let latLng = {
                  lat: l.lat,
                  lng: l.lng + (hash[key] || 0),
                };
                let marker = new google.maps.Marker({
                  map: map,
                  icon: new google.maps.MarkerImage(
                    root.vendorAvatars[v.avatar_type || "0"].src,
                    null,
                    null,
                    null,
                    new google.maps.Size(36, 36)
                  ),
                  position: latLng,
                  vendor: {
                    ...v,
                    street: l.street,
                    covered_areas: l.covered_areas,
                    clinic_name: l.clinic_name,
                    type:
                      root.vendorAvatars[v.avatar_type || "0"].tooltip !==
                      "Default"
                        ? root.vendorAvatars[v.avatar_type || "0"].tooltip
                        : "",
                    connectText:
                      v.prevent_referrals ||
                      (v.prevent_public_referrals &&
                        !root.user.isVendor &&
                        !root.user.isAdmin)
                        ? ""
                        : "Connect With Vendor",
                  },
                  animation: google.maps.Animation.DROP,
                });
                marker.content = root.renderMarkerModal(marker);
                root.mapMarkers.push(marker);
                marker.addListener("click", function () {
                  root.markerClickHandler(marker);
                  infowindow.setContent(marker.content);
                  infowindow.open(map);
                  map.panBy(0, -90);
                  infowindow.setPosition(
                    new google.maps.LatLng(marker.lat, marker.lng)
                  );
                  this.latLng = { lat: marker.lat, lng: marker.lng };
                  vm.clearSelectedAreas();
                  (marker.vendor.covered_areas || []).forEach((area) => {
                    vm.drawCoveredArea(area);
                  });
                });
              });
            }
          }.bind(this)
        );
      }
      if (this.currentLocation) {
        new google.maps.Marker({
          map: map,
          icon: new google.maps.MarkerImage(
            "/images/map-avatars/current-location.gif",
            null,
            null,
            null,
            new google.maps.Size(24, 37.5)
          ),
          position: this.currentLocation,
        });
      } else if (navigator.geolocation && google && google.maps) {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            this.currentLocation = {
              lat: position.coords.latitude,
              lng: position.coords.longitude,
            };
            new google.maps.Marker({
              map: map,
              icon: new google.maps.MarkerImage(
                "/images/map-avatars/current-location.gif",
                null,
                null,
                null,
                new google.maps.Size(24, 37.5)
              ),
              position: this.currentLocation,
            });
          },
          (error) => {
            console.log(error.message);
          },
          {
            enableHighAccuracy: false,
            timeout: 10000,
            maximumAge: Infinity,
          }
        );
      }
      if (map) {
        google.maps.event.trigger(map, "idle");
      }
    },
    showVendor: function (vendor, noPan, otherlocation = false) {
      window.scroll({
        top: 0,
        left: 0,
        behavior: "smooth",
      });
      if (!this.selectedVendor || this.selectedVendor.id !== vendor.id) {
        this.locationIndex = 0;
      }
      this.selectedVendor = vendor;
      this.closeInfoPopup();
      if (vendor.locations.length && (!noPan || otherlocation)) {
        if (this.locationIndex >= vendor.locations.length) {
          this.locationIndex = 0;
        }
        map.setCenter({
          lat: vendor.locations[this.locationIndex].lat,
          lng: vendor.locations[this.locationIndex].lng,
        });
        this.clearSelectedAreas();
        (vendor.locations[this.locationIndex].covered_areas || []).forEach(
          (area) => {
            this.drawCoveredArea(area);
          }
        );
        if (otherlocation) {
          const marker = this.mapMarkers.filter(
            (marker) =>
              marker.vendor.id == vendor.id &&
              marker.position.lat() ==
                vendor.locations[this.locationIndex].lat &&
              marker.position.lng() == vendor.locations[this.locationIndex].lng
          )[0];
          google.maps.event.trigger(marker, "click");
        }
        if (!noPan) {
          map.setZoom(this.closeZoom);
        }
        this.locationIndex++;
      }
      this.searchVendors(false, true);
    },
    viewVendorProfile(vendor) {
      this.selectedVendorToShow = vendor;
      this.$store.dispatch("showModal", "vendor-profile-modal");
    },
    searchVendors: function (ignoreSelectedVendor, showVendorClicked) {
      if (
        this.searchText.trim().length > 0 &&
        this.searchText.trim().length < 3
      ) {
        this.errorText = "Please enter at least 3 characters.";
        this.hasError = true;
        return;
      }
      if (
        this.searchByCity &&
        this.searchText.trim().length &&
        !showVendorClicked
      ) {
        this.resultVendors = this.activeVendors;
        return this.navigateToCity().then((success) => {
          if (success) {
            this.plotMarkers();
          }
          return;
        });
      }
      this.submittedSearchtext = this.searchText.trim();
      this.ignoreSelectedVendor = ignoreSelectedVendor;
      if (this.activeVendors.length) {
        const selectedVendor = ignoreSelectedVendor
          ? false
          : this.selectedVendor;
        if (this.searchText.trim().length == 0) {
          this.resultVendors = this.activeVendors.filter((v) => {
            if (!selectedVendor || selectedVendor.id !== v.id) {
              return v;
            }
          });
          if (!showVendorClicked) {
            this.refreshMap(true);
          }
        } else if (this.searchText.trim().length >= 3) {
          let text = this.searchText.trim();
          if (text.indexOf("#") == 0 && this.user.isAdmin) {
            text = text.replace("#", "");
            this.resultVendors = this.activeVendors.filter((v) => {
              const hasServicesMatch =
                v.services.filter(
                  (service) =>
                    service.private &&
                    service.name.toLowerCase() == text.toLowerCase()
                ).length > 0;
              if (
                hasServicesMatch &&
                (!selectedVendor || selectedVendor.id !== v.id)
              ) {
                return v;
              }
            });
          } else if (text.indexOf("#") == 0) {
            text = text.replace("#", "c3_secret_");
            this.secretRunning = true;
            return this.$store
              .dispatch("vendors/search", { searchWord: text })
              .then((vendors) => {
                this.secretRunning = false;
                if (vendors) {
                  this.resultVendors = vendors;
                  if (!showVendorClicked) {
                    this.refreshMap(true);
                  }
                  if (ignoreSelectedVendor) {
                    const mapCenter = { lat: 0.0, lng: 0.0, total: 0 };
                    this.resultVendors.forEach((v) => {
                      if (v.locations.length) {
                        v.locations.forEach((location) => {
                          mapCenter.lat = mapCenter.lat + location.lat;
                          mapCenter.lng = mapCenter.lng + location.lng;
                          mapCenter.total++;
                        });
                      }
                    });
                    if (mapCenter.total > 0) {
                      if (this.followResults) {
                        map.setCenter({
                          lat: mapCenter.lat / mapCenter.total,
                          lng: mapCenter.lng / mapCenter.total,
                        });
                        this.getBoundsZoomLevel();
                      }
                    } else {
                      this.errorText = "No vendors found!";
                      this.hasError = true;
                    }
                  }
                }
              });
          } else {
            this.resultVendors = this.activeVendors.filter((v) => {
              const hasServicesMatch =
                v.services.filter((service) =>
                  service.name.toLowerCase().includes(text.toLowerCase())
                ).length > 0;
              if (
                (v.name.toLowerCase().includes(text.toLowerCase()) ||
                  v.bioExcerpt.toLowerCase().includes(text.toLowerCase()) ||
                  hasServicesMatch) &&
                (!selectedVendor || selectedVendor.id !== v.id)
              ) {
                return v;
              }
            });
          }
          if (!showVendorClicked) {
            this.refreshMap(true);
          }
        }
        if (ignoreSelectedVendor) {
          const mapCenter = { lat: 0.0, lng: 0.0, total: 0 };
          this.resultVendors.forEach((v) => {
            if (v.locations.length) {
              v.locations.forEach((location) => {
                mapCenter.lat = mapCenter.lat + location.lat;
                mapCenter.lng = mapCenter.lng + location.lng;
                mapCenter.total++;
              });
            }
          });
          if (mapCenter.total > 0) {
            if (this.followResults) {
              map.setCenter({
                lat: mapCenter.lat / mapCenter.total,
                lng: mapCenter.lng / mapCenter.total,
              });
              this.getBoundsZoomLevel();
            }
          } else {
            this.errorText = "No vendors found!";
            this.hasError = true;
          }
        }
      }
    },
    clearSearch: function () {
      this.searchText = "";
      this.hasError = false;
      this.errorText = "";
      this.searchVendors(true);
    },
    removeError: function () {
      if (
        this.searchText.trim().length == 0 ||
        this.searchText.trim().length >= 3
      ) {
        this.errorText = "Please enter at least 3 characters.";
        this.hasError = false;
      }
    },
    speechUpdate: function (text) {
      this.searchText = text;
    },
    speechEnd: function ({ text }) {
      this.searchText = text;
      this.searchVendors(true);
    },
    renderMarkerModal(marker) {
      var ComponentClass = Vue.extend(MapMarkerInfo);
      var instance = new ComponentClass({
        propsData: {
          data: marker.vendor,
          location: marker.getPosition(),
          getLocation: this.getDirectionsFromTooltip,
          connectVendor: this.connectWithVendorFromTooltip,
          openBooking: this.openBooking,
          showCard: this.scrollToElement.bind(this),
          showOtherLocations: this.showVendor,
        },
      });
      instance.$mount();
      return instance.$el;
    },
    goToReferrals: function () {
      this.$router.push({ path: "/orders" });
    },
    navigateToCity: function () {
      return axios
        .get(
          `https://maps.googleapis.com/maps/api/geocode/json?address=${this.searchText.trim()}&key=${
            "AIzaSyCXAVpVS4FLtDCukiDoOAnG-Mb4vnI91Zk" ||
            process.env.VUE_APP_GOOGLE
          }`
        )
        .then((res) => {
          if (res.status == 200 && res.data.status === "OK") {
            const cityLocation = ((res.data.results[0] || {}).geometry || {})
              .location;
            if (cityLocation) {
              map.setCenter(cityLocation);
              map.setZoom(this.closeZoom);
              return Promise.resolve(true);
            } else {
              this.errorText = "Address not found!";
              this.hasError = true;
              return Promise.resolve(false);
            }
          } else {
            this.errorText = "Address not found!";
            this.hasError = true;
            return Promise.resolve(false);
          }
        })
        .catch(() => {
          this.errorText =
            "Something went wrong. Please refresh the page and try again";
          this.hasError = true;
        });
    },
    handleScroll: function () {
      if (window.scrollY > screen.availHeight / 4) {
        this.areaShown = "vendors";
      } else {
        this.areaShown = "map";
      }

      const chatButton = document.getElementById(
        "hubspot-messages-iframe-container"
      );
      if (chatButton) {
        if (window.scrollY < 80) {
          chatButton.style.cssText += "display: none !important;";
        } else {
          chatButton.style.cssText = chatButton.style.cssText.replace(
            "display: none !important;",
            ""
          );
        }
      }
    },
    scrollToElement: function (main = false) {
      if (main) {
        this.$refs.mainVendorCard.scrollIntoView({ behavior: "smooth" });
        this.movingToCard = true;
        setTimeout(() => {
          this.movingToCard = false;
        }, 2000);
        return;
      }
      if (this.areaShown == "vendors") {
        window.scroll({
          top: 0,
          left: 0,
          behavior: "smooth",
        });
      } else {
        this.$refs.vendorsCards.scrollIntoView({ behavior: "smooth" });
      }
    },
    getDirections: function () {
      this.getDirectionsClicked = !this.getDirectionsClicked;
    },
    getDirectionsFor: function (address) {
      const routeURL = `https://www.google.com/maps/dir/${this.currentLocation.lat},${this.currentLocation.lng}/${address.lat},${address.lng}`;
      window.open(routeURL, { target: "_blank" });
      this.getDirections();
    },
    getAddress: function ({ street, city, state, zip, country }) {
      let address = "";
      if ((street || "").trim()) {
        address = `${address}${street}, `;
      }
      if ((city || "").trim()) {
        address = `${address}${city}, `;
      }
      if ((state || "").trim()) {
        address = `${address}${state} `;
      }
      if ((zip || "").trim()) {
        address = `${address}${zip}, `;
      }
      if ((country || "").trim()) {
        address = `${address}${country}`;
      }
      return address;
    },
    hideDropdownList: function () {
      this.getDirectionsClicked = false;
    },
    getDirectionsFromTooltip: function (location) {
      const routeURL = `https://www.google.com/maps/dir/${
        this.currentLocation.lat
      },${this.currentLocation.lng}/${location.lat()},${location.lng()}`;
      window.open(routeURL, { target: "_blank" });
    },
    connectWithVendorFromTooltip: function (vendor, location) {
      this.$router.push(
        `/vendors/${
          vendor.id
        }/order?lat=${location.lat()}&lng=${location.lng()}`
      );
    },
    openBooking(link) {
      if (link.indexOf("http") > -1) {
        window.open(link, "_blank");
      } else {
        window.open(`https://${link}`, "_blank");
      }
    },
    getBoundsZoomLevel: function () {
      const bounds = new google.maps.LatLngBounds();
      this.mapMarkers.forEach((item) => bounds.extend(item.getPosition()));
      map.fitBounds(bounds);
    },
    handleMapViewChange: function () {
      if (markerCluster) {
        markerCluster.clearMarkers();
        markerCluster.setMap(null);
        markerCluster = null;
      }
      map.addListener("maptypeid_changed", () => {
        const shouldSelectSatellite =
          map.getMapTypeId() == "hybrid" && this.firstMapTypeChange;
        if (shouldSelectSatellite) {
          setTimeout(() => {
            map.setMapTypeId("satellite");
          }, 0);
          this.firstMapTypeChange = false;
        }
      });
      map.addListener("renderingtype_changed", () => {
        if (map.getRenderingType() === "VECTOR") {
          const buttons = [
            [
              "rotate",
              -20,
              "",
              "/images/map-controls/rotate-right.svg",
              "Rotate map clockwise",
            ],
            [
              "rotate",
              20,
              "ui-flip",
              "/images/map-controls/rotate-right.svg",
              "Rotate map counterclockwise",
            ],
            [
              "tilt",
              20,
              "",
              "/images/map-controls/tilt-down.svg",
              "Tilt map down",
            ],
            [
              "tilt",
              -20,
              "",
              "/images/map-controls/tilt-up.svg",
              "Tilt map up",
            ],
          ];

          const controlDiv = document.createElement("div");
          controlDiv.classList.add("vector-map-control");

          const addControl = ([mode, amount, className, src, title]) => {
            const controlUI = document.createElement("button");
            controlUI.classList.add("ui-button");
            if (className) {
              controlUI.classList.add(className);
            }
            controlUI.title = title;

            const controlImage = document.createElement("img");
            controlImage.classList.add("ui-button-img");
            controlImage.src = src;
            controlUI.appendChild(controlImage);

            controlUI.addEventListener("click", () => {
              this.adjustMap(mode, amount);
            });

            controlDiv.appendChild(controlUI);

            const separatorDiv = document.createElement("div");
            separatorDiv.classList.add("ui-separator");
            controlDiv.appendChild(separatorDiv);
          };

          buttons.forEach(addControl);

          map.controls[google.maps.ControlPosition.RIGHT_CENTER].push(
            controlDiv
          );
        }
      });

      map.addListener("idle", () => {
        if (map && map.center) {
          this.latLng = { lat: map.center.lat(), lng: map.center.lng() };
        }
        const displayedMarkers = [];
        const visibleBounds = map.getBounds();
        if (!visibleBounds) {
          return;
        }
        this.mapMarkers.forEach((marker) => {
          if (!visibleBounds.contains(marker.getPosition())) {
            marker.setMap(null);
          }
        });

        this.mapMarkers.forEach((marker) => {
          if (visibleBounds.contains(marker.getPosition())) {
            if (marker.getMap() == null) {
              marker.setMap(map);
            }
            displayedMarkers.push(marker);
          }
        });
        this.updateClusterMarkers(displayedMarkers);
      });
    },
    adjustMap: function (mode, amount) {
      switch (mode) {
        case "tilt":
          if (map.getTilt() <= 0 && amount < 0) {
            return;
          }
          map.setTilt(map.getTilt() + amount);
          break;
        case "rotate":
          map.setHeading(map.getHeading() + amount);
          break;
        default:
          break;
      }
    },
    updateClusterMarkers: function (newMarkers) {
      if (!markerCluster) {
        markerCluster = new MarkerClusterer({ map, newMarkers });
        return;
      }
      markerCluster.clearMarkers();
      markerCluster.addMarkers(newMarkers);
    },
    clearSelectedAreas: function () {
      this.drawnShapes.forEach((shape) => {
        shape.setMap(null);
      });
      this.drawnShapes = [];
    },
    drawCoveredArea(area) {
      switch (area.type) {
        case "circle":
          this.drawCircle(area);
          break;
        case "rectangle":
          this.drawRectangle(area);
          break;
        case "polygon":
          this.drawPolygon(area);
          break;
        default:
          break;
      }
    },
    drawCircle(area) {
      const circle = new google.maps.Circle({
        strokeColor: "#FF0000",
        strokeOpacity: 0.8,
        strokeWeight: 1,
        fillColor: "#FF0000",
        fillOpacity: 0.2,
        map,
        center: area.center,
        radius: area.radius,
      });
      this.drawnShapes.push(circle);
    },
    drawRectangle(area) {
      const rectangle = new google.maps.Rectangle({
        strokeColor: "#FF0000",
        strokeOpacity: 0.8,
        strokeWeight: 1,
        fillColor: "#FF0000",
        fillOpacity: 0.2,
        map,
        bounds: {
          north: area.ne.lat,
          east: area.ne.lng,
          south: area.sw.lat,
          west: area.sw.lng,
        },
      });
      this.drawnShapes.push(rectangle);
    },
    drawPolygon(area) {
      const polygon = new google.maps.Polygon({
        strokeColor: "#FF0000",
        strokeOpacity: 0.8,
        strokeWeight: 1,
        fillColor: "#FF0000",
        fillOpacity: 0.2,
        map,
        paths: area.paths,
      });
      this.drawnShapes.push(polygon);
    },
  },
};
</script>
<style lang="scss" scoped>
.blink-opacity {
  animation: blinker 2.5s linear infinite;
}

@keyframes blinker {
  50% {
    opacity: 0;
  }
}
.loader {
  top: 0;
  left: 0;
  right: 0;
  border: 0;
  z-index: 99999;
  background: rgba(0, 0, 0, 0.7);
  color: whitesmoke;
  flex-direction: column;
}

.detect-location-container {
  position: absolute;
  top: 150px;
  width: 100%;

  .alert {
    display: inline-block;
    background: #fdf3d9ab;
    font-size: 17px;
  }
}

#dropdownSelectVendorMenu {
  max-width: 100%;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}
.select-vendor-menu {
  left: auto !important;
  right: 11px !important;
  max-height: 400px;
  overflow-y: auto;
  transform: none !important;
  top: 42px !important;

  .dropdown-item {
    white-space: pre-wrap;
  }
}

.iframe-control {
  width: 90%;
  margin-left: auto;
  margin-right: auto;
}

.directions-list {
  max-height: 200px;
  overflow-y: auto;
  position: absolute;
  max-width: 90%;
  z-index: 5;
}

.map-iframe {
  .map {
    margin-top: 0;
  }

  .search-box {
    top: 68px;
  }
}

.map {
  height: calc(100vh - 70px) !important;
  @media (max-width: 1024px) {
    margin-top: -23px;
  }
}

.back-to-top-btn {
  position: fixed;
  bottom: 30px;
  left: 50%;
  transform: translateX(-50%);
  opacity: 0.9;
  z-index: 1;
}

.search-box {
  z-index: 0;
  position: absolute;
  left: 0;
  right: 0;
  top: 0;
  transition: all 0.7s ease-in-out;
  width: 200px;
  overflow: visible;
  padding: 0 !important;

  @media (max-width: 767px) {
    width: 130px;
    input {
      padding-right: 56px !important;
    }
  }

  .cancel-icon {
    display: none;
  }

  input {
    padding-right: 70px;
    background-color: rgba(255, 255, 255, 0.9);
  }

  &:focus,
  &.focused {
    width: 100%;
    overflow: visible;

    .cancel-icon {
      display: block;
    }

    .invalid-feedback {
      display: inline-block !important;
    }
  }

  input {
    border-radius: 20px !important;
    transition: background 0.2s ease-in-out;

    &:focus {
      background-color: #ffffff;
    }

    &:hover,
    &:focus {
      outline: 0 !important;
      box-shadow: none !important;
    }
  }

  .invalid-feedback {
    margin-top: 8px;
    display: none !important;
    width: auto;
  }
}

.current-location-btn {
  position: absolute;
  right: 16px;
  top: 10px;

  background-color: rgba(255, 255, 255, 0.8);

  &:focus {
    background-color: rgba($color: #000000, $alpha: 0.3);
  }

  .current-location-img {
    width: 26px;
  }
}

.search-settings-btn {
  background: white;
  border-radius: 50%;
  padding: 2px;
  line-height: 0;
}

.search-setting-menu {
  top: 10px !important;
  left: -40px !important;
}

.tooltip-info {
  background: white;
  border-radius: 50%;
}

.speech-btn {
  z-index: 9;
  position: absolute;
  right: 69px;
  top: 7px;
}

.search-button {
  margin-left: -40px;
  z-index: 9;
  outline: 0 !important;
  box-shadow: none !important;
  background-color: transparent;
}

.vendor-logo-col {
  max-width: 50%;
  margin-left: auto;
  margin-right: auto;
}

.service-item {
  padding: 3px;
}

.service-item-container {
  height: 100%;
  background-color: #5c6bc0;
  color: #fff;
  border-radius: 4px;
  padding: 7px;
  font-size: 0.85em;
}

.vendor-cards {
  width: 90%;
  margin-left: auto;
  margin-right: auto;

  .row {
    overflow-x: auto;
    white-space: nowrap;
  }

  .card-column {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    grid-auto-rows: 1fr;
    grid-column-gap: 0;
    grid-row-gap: 26px;

    @media (max-width: 1500px) {
      grid-template-columns: repeat(3, 1fr);
    }

    @media (max-width: 886px) {
      grid-template-columns: repeat(2, 1fr);
    }

    @media (max-width: 594px) {
      grid-template-columns: repeat(1, 1fr);
    }
  }
}

.cancel-icon {
  z-index: 99;
  position: absolute;
  right: 99px;
  top: 50%;
  transform: translateY(-50%);
  color: crimson;
  cursor: pointer;
}
.list_item {
  transition: 0.3s;
  &:hover {
    background-color: #ddd !important;
  }
}

@media (max-width: 767px) {
  .search-button {
    margin-left: -32px;
    padding-left: 4px;
    padding-right: 8px;
  }

  .cancel-icon {
    right: 105px;
  }

  .speech-btn {
    right: 74px;
  }

  ::placeholder {
    opacity: 0;
    /* Firefox */
  }

  :-ms-input-placeholder {
    /* Internet Explorer 10-11 */
    opacity: 0;
  }

  ::-ms-input-placeholder {
    /* Microsoft Edge */
    opacity: 0;
  }
}
</style>

<style lang="scss">
.map {
  [aria-label="Open this area in Google Maps (opens a new window)"] {
    display: none !important;
  }
  [aria-label="Change map style"] {
    padding: 8px 20px !important;
  }
  [aria-label="Change map style"],
  [aria-label="Drag Pegman onto the map to open Street View"],
  > div > div > div:nth-child(14) > div > div > div {
    background-color: rgba(255, 255, 255, 0.8) !important;
  }

  .gm-style {
    .gmnoprint {
      li {
        background-color: rgba(255, 255, 255, 0.9) !important;
      }
      ul {
        background-color: transparent !important;
      }
    }
  }
  .vector-map-control {
    display: flex;
    flex-direction: column;
    margin-right: 10px;
    box-shadow: rgba(0, 0, 0, 0.3) 0px 1px 4px -1px;
    border-radius: 2px;
    cursor: pointer;
    width: 40px;
    background-color: rgba(255, 255, 255, 0.8);

    .ui-button {
      border: 0;
      background-color: transparent;
      width: 40px;
      height: 40px;

      &.disabled {
        opacity: 0.5;
      }
      .ui-button-img {
        width: 20px;
      }

      &:hover {
        .ui-button-img {
          filter: brightness(0.5);
        }
      }
    }
    .ui-flip {
      transform: scaleX(-1);
    }
    .ui-separator {
      position: relative;
      overflow: hidden;
      width: 30px;
      height: 1px;
      margin: 0px 5px;
      background-color: rgb(230, 230, 230);
      top: 0px;
    }
  }
}
</style>
